import { decodeHtml } from "./html-entities";
import { escapeInlineText } from "./inline-html-vocabulary";

/**
 * Upgrade a legacy plain-text body to body-vocabulary HTML — the stored text
 * keeps the paragraph structure the legacy renderer gave it (blank line = new
 * paragraph, single newline = line break). Text is escaped: legacy bodies are
 * plain text, so any angle brackets in them are literal characters, never
 * markup. Pure (no cheerio) so BOTH the client editor and the server-only
 * render boundary can call it.
 */
export function plainTextToBodyHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block !== "")
    .map(
      (block) =>
        `<p>${block.split(/\n/).map(escapeInlineText).join("<br>")}</p>`
    )
    .join("");
}

/** A `<tag ...>` or `</tag>` opener for a recognized body/inline vocabulary tag
 *  — the markup the editor and sanitizer actually produce. Deliberately NOT
 *  "any letter after `<`": a legacy plain-text row like `ask for <John>` or
 *  `price < $10` must read as plain text, not markup, or it would be dropped. */
const BODY_HTML_TAG_RE =
  /<\/?(?:p|br|b|strong|i|em|u|a|span|ul|ol|li|blockquote|h[1-6]|div)(?:\s|>|\/)/i;

/**
 * Normalize a stored body value to safe body-vocabulary HTML: a value that
 * already contains recognized markup passes through verbatim; a legacy
 * plain-text row (saved via the old plain textarea, with raw `\n` line breaks,
 * possibly containing stray `<`/`&`) is escaped + upgraded to body HTML.
 *
 * The ONE "is this legacy plain text?" rule, shared by every surface:
 *  - the editor seed (`VenueParkingSettings`) — so Tiptap keeps the paragraph
 *    structure instead of collapsing newlines and dropping a `<word>` fragment;
 *  - the render boundary (`VenueRichTextBody`) — so `sanitizeBodyHtml`'s cheerio
 *    parse never sees an unknown `<John>` element to unwrap-and-drop. Without
 *    this the public page silently loses `<word>`-shaped legacy text.
 */
export function seedBodyHtml(value: string): string {
  return !value || BODY_HTML_TAG_RE.test(value)
    ? value
    : plainTextToBodyHtml(value);
}

/**
 * Flatten a stored body value to plain text, preserving its line structure —
 * the client-safe counterpart to `bodyHtmlToPlainText` (which pulls cheerio and
 * so is server-only). For the plain-text show-description surfaces that render
 * in the browser (the venue card previews/modals, the show-row subtitle).
 *
 * `seedBodyHtml` first upgrades a legacy plain-text row so its raw newlines
 * become real `<br>`/`<p>` structure (and a literal `<VIP>` is escaped, not
 * read as a tag) instead of collapsing to one run-on line. Then: `<br>` becomes a newline and a closing block a blank line (so a
 * `line-clamp` preview still truncates cleanly and a `whitespace-pre-*` modal
 * shows the breaks), a list item becomes a `- ` line like the server
 * flattener's, remaining tags drop, and entities decode (so an editor-saved
 * `&amp;`/`&nbsp;` reads as `&`/a space, never the raw entity). A plain row with
 * no newlines is unchanged.
 *
 * Reads a RAW stored value, not sanitized HTML, so it decodes `&nbsp;` itself
 * (the server sanitizer collapses that to a space, but these surfaces never run
 * it); `decodeHtml` owns the serializer family's round-trip pair and is left as
 * is.
 */
export function bodyHtmlToPlainTextClient(value: string): string {
  return decodeHtml(
    seedBodyHtml(value)
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<li\b[^>]*>/gi, "- ")
      .replace(/<\/li>/gi, "\n")
      .replace(/<\/(?:p|h[1-6]|ul|ol|blockquote|div)>/gi, "\n\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
  )
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}
